'use client';

/**
 * The institution's Google Tag Manager container, on the public marketing
 * pages only (`lib/publicSite.ts`). It carries the site's Google Analytics
 * and Meta Pixel; it was dropped in the August v2 cutover and restored on
 * 2026-10-13.
 *
 * Two rules keep it off the application:
 *
 * - It loads only in a document that *began* on a public page. A document
 *   that started in the app carries the app's CSP, which refuses the tag
 *   manager, so trying would only produce violations.
 * - Once loaded it cannot be unloaded, and Analytics records client-side
 *   navigations as page views. So leaving the public site is a full page
 *   load, which discards the tag manager before any application URL — with
 *   its tokens and record ids — can be reported: links are handled here,
 *   buttons call `leavePublicSite`, and a client-side arrival in the app
 *   with the tag manager loaded reloads the page as a last resort.
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

import { browserNavigation, isPublicSitePath } from '@/lib/publicSite';

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID?.trim();
const SCRIPT_ID = 'public-tag-manager';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Whether this document began on a public page. Fixed at first render. */
let documentBeganPublic: boolean | null = null;

function tagManagerLoaded(): boolean {
  return document.getElementById(SCRIPT_ID) !== null;
}

function loadTagManager(containerId: string) {
  if (tagManagerLoaded()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(containerId)}`;
  document.head.appendChild(script);
}

/**
 * Capture-phase click handler: a same-origin link into the application,
 * clicked after the tag manager loaded, is left to the browser as an
 * ordinary navigation instead of Next's client-side one.
 */
function leaveByFullPageLoad(event: MouseEvent) {
  if (!tagManagerLoaded() || event.defaultPrevented) return;
  const anchor = (event.target as Element | null)?.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const destination = new URL(anchor.href, window.location.href);
  if (destination.origin !== window.location.origin) return;
  if (isPublicSitePath(destination.pathname)) return;
  // Stopping here keeps the click from reaching Next's Link handler, so the
  // browser follows the href itself.
  event.stopPropagation();
}

export default function PublicTagManager() {
  const pathname = usePathname();

  useEffect(() => {
    if (!GTM_ID) return;
    if (documentBeganPublic === null) {
      documentBeganPublic = isPublicSitePath(window.location.pathname);
    }
    if (!isPublicSitePath(pathname)) {
      // Safety net: something reached the application client-side with the
      // tag manager loaded. Reload, so it reports nothing beyond this page.
      if (tagManagerLoaded()) browserNavigation.reload();
      return;
    }
    if (documentBeganPublic) {
      loadTagManager(GTM_ID);
    }
  }, [pathname]);

  useEffect(() => {
    if (!GTM_ID) return;
    document.addEventListener('click', leaveByFullPageLoad, true);
    return () => document.removeEventListener('click', leaveByFullPageLoad, true);
  }, []);

  return null;
}

/** Test hook: forget which page this document began on. */
export function resetPublicTagManagerForTests() {
  documentBeganPublic = null;
}
